import React, { useState } from 'react';
import { cloneDeep } from 'es-toolkit/object';

import { toastify, useTranslation } from '@testsigma/atoms';

import { SalesforceConnectionsService } from '@modules/salesforce/services';
import {
  SalesforceConnection,
  SalesforceConnectionEdition,
  SalesforceConnectionType,
} from '@modules/salesforce/models';

import { getConnectionEnv } from '../../utils/utils';

import {
  SelectedSalesForceConnection,
  UserConnectionModalScreens,
  useSFConnectionState,
} from '@contexts/sf-connection-context';
import { useOAuthFlow } from '@modules/salesforce/shared/hooks/use-oauth-flow';

interface UseEditConnectionProps {
  isSaveOnlyMode: boolean;
  isTestConnection?: boolean;
}

export const useEditConnection = (props: UseEditConnectionProps) => {
  const { isSaveOnlyMode, isTestConnection } = props;
  const {
    handleUpdateOverlayMode,
    handleUpdateUserConnectionModalScreen,
    selectedConnection,
    handleUpdateSelectedConnection,
    handleReload,
    handleConnectionError,
  } = useSFConnectionState();
  const [testIsConnecting, setTestIsConnecting] = useState<boolean>();
  const { t } = useTranslation();

  const [isConnecting, setIsConnecting] = useState<boolean>();

  const handleClose = (event: React.SyntheticEvent) => {
    event.preventDefault();

    if (selectedConnection?.reload) {
      handleReload();
    }

    handleUpdateSelectedConnection(null);
    handleUpdateOverlayMode('none');
  };

  const handleTestFailure = (error?: string) => {
    const screenName =
      UserConnectionModalScreens.CONNECTION_AUTH_FAILURE_SCREEN;
    handleUpdateUserConnectionModalScreen(screenName);
    handleConnectionError(error);
  };

  const handleTestSuccess = () => {
    const screenName = UserConnectionModalScreens.CONNECTION_TEST_SUCCESSFUL;
    handleUpdateUserConnectionModalScreen(screenName);
  };

  const handleSuccess = (sfConnectionData: SalesforceConnection) => {
    setIsConnecting(false);

    const response: SelectedSalesForceConnection = cloneDeep(sfConnectionData);
    response.salesforceConnectionEdition = getConnectionEnv(sfConnectionData);
    response.reload = true;
    response.hasFailed = false;
    handleUpdateSelectedConnection(response);

    if (isSaveOnlyMode) {
      toastify(t('message.connection.update.success'), {
        type: 'success',
        position: 'top-center',
      });
      return;
    }

    handleUpdateUserConnectionModalScreen(
      UserConnectionModalScreens.CONNECTION_UPDATE_SUCCESS_SCREEN,
    );
  };

  const handleFailure = (
    sfConnectionData: SalesforceConnection,
    error: string,
  ) => {
    setIsConnecting(false);

    const response: SelectedSalesForceConnection = cloneDeep(sfConnectionData);
    response.reload = false;
    response.hasFailed = true;
    handleUpdateSelectedConnection(response);
    handleConnectionError(error);

    if (isSaveOnlyMode) {
      toastify(t('message.connection.update.failure'), {
        type: 'error',
        position: 'top-center',
      });
      return;
    }

    handleUpdateUserConnectionModalScreen(
      UserConnectionModalScreens.CONNECTION_AUTH_FAILURE_SCREEN,
    );
  };

  const handleUpdate = (sfConnectionData: SalesforceConnection) => {
    setIsConnecting(true);

    const data = cloneDeep(sfConnectionData);

    data.performValidation = !isSaveOnlyMode;
    data.isSandbox =
      data.salesforceConnectionEdition === SalesforceConnectionEdition.SANDBOX;
    data.isScratchOrg =
      data.salesforceConnectionEdition ===
      SalesforceConnectionEdition.SCRATCH_ORG;

    SalesforceConnectionsService.update(data)
      .then(handleSuccess)
      .catch((error: string) => {
        handleFailure(data, error);
      })
      .finally(() => {
        setIsConnecting(false);
      });
  };

  const handleTestConnection = (sfConnectionData: SalesforceConnection) => {
    setTestIsConnecting(true);

    const data = cloneDeep(sfConnectionData);

    data.performValidation = !isSaveOnlyMode;
    data.isSandbox =
      data.salesforceConnectionEdition === SalesforceConnectionEdition.SANDBOX;
    data.isScratchOrg =
      data.salesforceConnectionEdition ===
      SalesforceConnectionEdition.SCRATCH_ORG;
    handleUpdateSelectedConnection(data);

    SalesforceConnectionsService.testSFConnection(data)
      .then((response: SalesforceConnection) => {
        if (response?.accessToken) {
          handleTestSuccess();
        } else {
          handleTestFailure();
        }
      })
      .catch((error) => {
        handleTestFailure(error);
      })
      .finally(() => {
        setTestIsConnecting(false);
      });
  };

  const { handleOAuthLogin, isConnecting: isConnectingOAuth } = useOAuthFlow({
    onAuthComplete: !isTestConnection ? handleUpdate : handleTestConnection,
    onAuthError: handleFailure,
  });
  const handleUsernamePasswordLogin = (
    sfConnectionData: SalesforceConnection,
  ) => {
    isTestConnection
      ? handleTestConnection(sfConnectionData)
      : handleUpdate(sfConnectionData);
  };

  return {
    isConnecting: isConnectingOAuth || isConnecting,
    hasConnectionFailed: selectedConnection?.hasFailed,
    handleUsernamePasswordLogin,
    handleUpdate,
    handleOAuthLogin,
    handleClose,
    testIsConnecting,
  };
};
